import { Col, Row } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ROLES, SEARCH, SELECTED_DEALER_ID, SELECTED_GROUP_ID } from 'constants/FilterConstants';
import { DEALER_STATUS } from 'constants/StatusConstants';
import { ALL_ROLES } from 'constants/UserConstants';
import * as actions from 'modules/users/actions/impersonate';
import { getImpersonateFilters } from 'modules/users/selectors';
import Button from 'ui/base/Button';
import Select from 'ui/base/Select';
import SearchInput from 'ui/fields/SearchInput';
import GroupSelect from 'ui/fields/selects/GroupSelect';
import PaginatedDealerSelect from 'ui/fields/selects/PaginatedDealerSelect';
import { humanize, ucFirst } from 'utils/global';

const [_, ...SELECTABLE_ROLES] = ALL_ROLES;

const rolesDataSource = SELECTABLE_ROLES.map(role => ({
  value: role,
  label: <FormattedMessage id={`role.${role}`} defaultMessage={humanize(ucFirst(role))} />,
}));

const Search = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = React.useState(false);
  const arrowIcon = expanded ? 'fb-icon--arrow-up' : 'fb-icon--arrow-down';

  const parameters = useSelector(getImpersonateFilters);

  const toggleExpandOnClick = React.useCallback(event => {
    event.preventDefault();
    setExpanded(value => !value);
  }, []);

  const handleTextOnSearch = React.useCallback(
    (searchingValue) => {
      dispatch(actions.setImpersonateModalFilter(SEARCH, searchingValue));
    },
    [dispatch]
  );

  const handleClearOnClick = React.useCallback(
    event => {
      event.preventDefault();
      dispatch(actions.clearImpersonateModalFilters());
    },
    [dispatch]
  );

  const handleGroupOnChange = React.useCallback(
    /** @param {string} groupId */
    groupId => {
      dispatch(
        actions.setImpersonateModalFilters([
          { field: SELECTED_GROUP_ID, value: groupId },
          { field: SELECTED_DEALER_ID, value: null },
        ])
      );
    },
    [dispatch]
  );

  const handleDealerOnChange = React.useCallback(
    /** @param {string} dealerId */
    dealerId => {
      dispatch(actions.setImpersonateModalFilter(SELECTED_DEALER_ID, dealerId));
    },
    [dispatch]
  );

  const handleRolesOnChange = React.useCallback(
    /** @param {import("modules/users/types/users.enum").UserRoleEnum} groupId */
    role => {
      dispatch(actions.setImpersonateModalFilter(ROLES, role ? [role] : []));
    },
    [dispatch]
  );

  return (
    <React.Fragment>
      <div className="bg-dark p-4 d-flex justify-content-between">
        <SearchInput
          className="w-50"
          defaultValue={parameters[SEARCH]}
          onSearch={handleTextOnSearch}
          placeholder="page.impersonate.search.placeholder"
        />
        <Button ghost onClick={toggleExpandOnClick} data-cy="advanced-filter-btn">
          <FormattedMessage id="page.home.filters.advanced" defaultMessage="Advanced filter" />
          <span className={arrowIcon} />
        </Button>
      </div>
      <div className={classNames('bg-light p-4', { 'd-none': !expanded })} data-cy="advanced-filter-panel">
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} md={12} xl={7}>
            <div className="mb-1">
              <FormattedMessage
                defaultMessage="Roles"
                id="audito.model.builder.sections.section.question.parameters.role"
              />
            </div>
            <Select
              allowClear
              data-cy="role-selector"
              placeholder={intl.formatMessage({
                id: 'page.user.roles.placeholder',
                defaultMessage: 'Select a role',
              })}
              options={rolesDataSource}
              onChange={handleRolesOnChange}
              value={parameters[ROLES].at(0)}
            />
          </Col>

          <Col xs={24} md={12} xl={6}>
            <div className="mb-1">
              <FormattedMessage id="group" defaultMessage="Group" />
            </div>
            <GroupSelect
              value={parameters[SELECTED_GROUP_ID]}
              onChange={handleGroupOnChange}
              allowClear
              placeholder={intl.formatMessage({
                defaultMessage: 'Select a group',
                id: 'group_config.group_select.placeholder',
              })}
            />
          </Col>

          <Col xs={24} md={12} xl={6}>
            <div className="mb-1">
              <FormattedMessage id="dealer" defaultMessage="Dealers" />
            </div>
            <PaginatedDealerSelect
              allowClear
              placeholder={intl.formatMessage({
                id: 'DealerSelect.selectDealership',
                defaultMessage: 'Select a dealership',
              })}
              onChange={handleDealerOnChange}
              value={parameters[SELECTED_DEALER_ID]}
              params={{ groupIds: [parameters[SELECTED_GROUP_ID]], status: [DEALER_STATUS.ACTIVE] }}
            />
          </Col>

          <Col xs={24} md={12} xl={5}>
            <Button ghost type="primary" block onClick={handleClearOnClick}>
              <FormattedMessage id="page.home.filters.clear" defaultMessage="Clear filters" />
            </Button>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default Search;
